import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { doRequest } from '@r1-frontend/do-request';
import { ResponseDto } from '@r1-frontend/api-domru/api-content/v1/geography/get-city-info';
import { createUnauthRequest } from '@r1-frontend/api-domru/api-request/v1/dealers/create-unauth-req';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { BaseButton } from '@r1-frontend/ui-react/components/buttons/baseButton';
import Input from '@r1-frontend/ui-react/components/input/input';
import { FlexBoxCol, FlexBoxRow } from '@r1-frontend/ui-react/components/layouts/flexBoxGrid';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { IOption } from '@r1-frontend/ui-react/components/select/select';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import { getCities } from '~/src/api/content/geography/get-all-cities';
import PopupResponse from '~/src/components/Promo/popups/response';
import { POLICY_PAGE_LINK } from '~/src/constants';

import * as ST from './styled';

interface ManiaCardProps {
    reward?: number,
    isPopupOpen: boolean,
    setIsPopupOpen: Dispatch<SetStateAction<boolean>>,
}

const ManiaPopup = ({ reward, isPopupOpen, setIsPopupOpen }: ManiaCardProps): JSX.Element => {
    const [isStartedTyping, setIsStartedTyping] = useState(false);
    const [cities, setCities] = useState<ResponseDto[]>([]);
    const initStatePopup = {
        showResponsePopup: false,
        responseTitle: '',
        responseText: '',
        buttonText: '',
    };
    const [{ showResponsePopup, responseTitle, responseText, buttonText }, setShowResponsePopup] = useState(initStatePopup);

    const [data, setData] = useState({
        dealerName: '',
        dealerPhone: { value: '', isValid: false },
        dealerMail: '',
        dealerId: '',
        dlrCityId: { value: '' },
        clientName: '',
        clientPhone: { value: '', isValid: false },
        clientCityId: '',
    });

    const close = useCallback(() => {
        setIsPopupOpen(false);
        setShowResponsePopup(initStatePopup);
    }, [initStatePopup]);

    const [acceptForm, setAcceptForm] = useState(false);
    const [isValid, setIsValid] = useState(false);

    const dealerNameRef = useRef<Input>(null);
    const dealerMailRef = useRef<Input>(null);
    const dealerIdRef = useRef<Input>(null);
    const clientNameRef = useRef<Input>(null);
    const clientCityIdRef = useRef();

    useEffect(() => {
        const fetchData = async() => {
            const allCities = await getCities();

            if (allCities) {
                setCities(allCities);
            }
        };
        fetchData();
        dataLayerPush({ event: EVENTS.UAevent,  category: CATEGORIES.dealerWidget, action : 'show_form', label: '' });
    }, []);

    useEffect(() => {
        checkIsValid();
    }, [data, acceptForm]);

    useEffect(() => {
        isPopupOpen && dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.dealerWidget,
            action: ACTIONS.transitionToForm,
            label: '',
        });
    }, [isPopupOpen]);

    const handleChange = useCallback((e: { target: { checked: boolean } }) => {
        setAcceptForm(e.target.checked);
    }, []);

    const handleSubmit = useCallback(async() => {
        const providerId = cities.find(item => item.id === +data.clientCityId)?.provider_id;

        try {
            if (!providerId) {
                throw new Error('The \'providerId\' arguments can not be empty');
            }

            await doRequest.apiRequest<unknown>(
                createUnauthRequest(providerId, {
                    ...data,
                    clientPhone: data.clientPhone.value,
                    dealerPhone: data.dealerPhone.value,
                    dlrCityId: data.dlrCityId.value,
                }),
            );
            dataLayerPush({ event: EVENTS.UAevent, category: CATEGORIES.dealerWidget, action : 'finished', label: '' });
            setShowResponsePopup({
                showResponsePopup: true,
                responseTitle: 'Спасибо, заявка принята',
                responseText: 'За статусом следите в личном кабинете. ' +
                    'Там же оставьте данные карты, на которую перечислять деньги за рекомендацию. ' +
                    'Инструкция и доступы к кабинету придут вам на e-mail.',
                buttonText: 'Закрыть',
            });
            setIsPopupOpen(false);
        } catch (e) {
            dataLayerPush({ event: EVENTS.UAevent, category: CATEGORIES.dealerWidget, action : 'error', label: '' });
            setShowResponsePopup({
                showResponsePopup: true,
                responseTitle: 'Ошибка',
                responseText: 'Не удалось отправить заявку. Пожалуйста, попробуйте еще раз.',
                buttonText: 'Закрыть',
            });
        }
    }, [cities, data]);

    const checkIsValid = useCallback(() => {
        let isValid = true;
        [dealerNameRef, dealerMailRef, dealerIdRef, clientNameRef].forEach(element => {
            if (element.current === undefined || element.current?.state?.hasError) {
                isValid = false;
            }
        });
        if (!data.dealerPhone.isValid || !data.clientPhone.isValid) {
            isValid = false;
        }
        if (data.dlrCityId.value === '' || data.clientCityId === '') {
            isValid = false;
        }
        if (!acceptForm) {
            isValid = false;
        }
        setIsValid(isValid);
    }, [acceptForm, data.clientCityId, data.clientPhone, data.dealerPhone, data.dlrCityId]);

    const setValue = useCallback((name: string, value: any) => {
        if (!isStartedTyping) {
            dataLayerPush({ event: EVENTS.UAevent, category: CATEGORIES.dealerWidget, action : ACTIONS.started, label: '' });
            setIsStartedTyping(true);
        }
        setData({ ...data, [name]: value });
    }, [data, isStartedTyping]);

    const recommendBtnText = 'Оформить рекомендацию';

    return <>
        <ST.ManiaPopup
            isOpen={isPopupOpen}
            onClose={close}
            closeColor={'#fff'}
            headerContent={'Рекомендуйте Дом.ру и получайте деньги на карту'}
            width={'900px'}
        >
            <ST.PopupBody data-test={'mania-popup'}>
                <ST.MainDescription>
                    Если по вашей рекомендации подключат услуги Дом.ру,<br/> мы переведем до {reward} р на вашу карту за
                    каждого подключившегося.
                </ST.MainDescription>
                <ST.SecondaryDescription>
                    Оставьте контакты человека, которому рекомендуете подключить услуги Дом.ру. Мы ему перезвоним.
                </ST.SecondaryDescription>
                <ST.FormHeader>Ваши данные</ST.FormHeader>
                <ST.FormGrid data-test={'personal'}>
                    <ST.Section>
                        <FlexBoxRow>
                            <FlexBoxCol xs={12} sm={4} md={4} lg={4}>
                                <Input
                                    errorMessage={'Ожидаются только буквы'}
                                    type={'name'}
                                    placeholder={'Имя'}
                                    ref={dealerNameRef}
                                    required={true}
                                    onChange={(e: string) => setValue('dealerName', e)}
                                    value={data.dealerName}
                                    className={'ym-disable-keys'}
                                />
                            </FlexBoxCol>
                            <FlexBoxCol xs={12} sm={4} md={4} lg={4}>
                                <ST.CitySelect
                                    placeholder={'Город'}
                                    options={cities.map<IOption<number>>((item: ResponseDto) => ({ label: item.name, value: item.id }))}
                                    onChange={(e: IOption<number>) => setValue('dlrCityId', e)}
                                    isSearchable={true}
                                />
                            </FlexBoxCol>
                        </FlexBoxRow>
                        <FlexBoxRow>
                            <FlexBoxCol xs={12} sm={4} md={4} lg={4}>
                                <ST.Phone
                                    name='phone'
                                    placeholder='Номер телефона'
                                    errorMessage='Введите корректный номер телефона'
                                    onChange={(value: any) => setValue('dealerPhone', value)}
                                    required={true}
                                    className={'ym-disable-keys'}
                                />
                            </FlexBoxCol>
                            <FlexBoxCol xs={12} sm={4} md={4} lg={4}>
                                <Input
                                    type='email'
                                    placeholder='Email'
                                    errorMessage='Введите корректный email'
                                    onChange={(value: string) => setValue('dealerMail', value)}
                                    value={data.dealerMail}
                                    ref={dealerMailRef}
                                    required={true}
                                    className={'ym-disable-keys'}
                                />
                            </FlexBoxCol>
                            <FlexBoxCol xs={12} sm={4} md={4} lg={4}>
                                <Input
                                    type='text'
                                    placeholder={'ID'}
                                    tooltipText={'Формируется при регистрации автоматически'}
                                    description={'Поле не обязательно для заполнения'}
                                    onChange={(value: string) => setValue('dealerId', value)}
                                    ref={dealerIdRef}
                                    value={data.dealerId}
                                />
                            </FlexBoxCol>
                        </FlexBoxRow>
                    </ST.Section>
                </ST.FormGrid>
                <ST.FormHeader>Кому рекомендуете</ST.FormHeader>
                <ST.FormGrid data-test={'recommend'}>
                    <ST.Section>
                        <FlexBoxRow>
                            <FlexBoxCol xs={12} sm={4} md={4} lg={4}>
                                <Input
                                    placeholder={'Имя'}
                                    onChange={(value: string) => setValue('clientName', value)}
                                    value={data.clientName}
                                    required={true}
                                    errorMessage={'Ожидаются только буквы'}
                                    type={'name'}
                                    ref={clientNameRef}
                                />
                            </FlexBoxCol>
                            <FlexBoxCol xs={12} sm={4} md={4} lg={4}>
                                <ST.Phone
                                    name='phone'
                                    placeholder='Номер телефона'
                                    errorMessage='Введите корректный номер телефона'
                                    onChange={(value: any) => setValue('clientPhone', value)}
                                    required={true}
                                />
                            </FlexBoxCol>
                            <FlexBoxCol xs={12} sm={4} md={4} lg={4}>
                                <ST.CitySelect
                                    placeholder={'Город'}
                                    options={cities.map<IOption<number>>((item: ResponseDto) => ({ label: item.name, value: item.id }))}
                                    onChange={(option: IOption<number>) => setValue('clientCityId', option.value)}
                                    ref={clientCityIdRef}
                                    required={true}
                                    isSearchable={true}
                                />
                            </FlexBoxCol>
                        </FlexBoxRow>
                        <FlexBoxRow>
                            <FlexBoxCol xs={12} sm={12} md={12} lg={12} data-test={'agreement-checkbox'}>
                                <ST.CheckboxTextMin
                                    onChange={(e: { target: { checked: boolean } }) => {
                                        handleChange(e);
                                        checkIsValid();
                                    }}
                                    checked={acceptForm}
                                >Согласен с условиями&nbsp;
                                    <a href='https://dealers.dom.ru/files/upload/oferta.pdf'
                                       target='_blank'
                                       rel='noreferrer'
                                    >оферты</a>
                                </ST.CheckboxTextMin>
                            </FlexBoxCol>
                        </FlexBoxRow>
                        <ST.FlexBoxRowMargin>
                            <FlexBoxRow>
                                <PolicyNote
                                    data-test='agreement'
                                    buttonText={recommendBtnText}
                                    agreeUrl={POLICY_PAGE_LINK}
                                />
                            </FlexBoxRow>
                            <FlexBoxCol xs={12} sm={12} md={12} lg={4}>
                                <BaseButton text={recommendBtnText} onClick={handleSubmit} disabled={!isValid}/>
                            </FlexBoxCol>
                        </ST.FlexBoxRowMargin>
                    </ST.Section>
                </ST.FormGrid>
            </ST.PopupBody>
        </ST.ManiaPopup>
        <PopupResponse
            isOpen={showResponsePopup}
            onClose={close}
            title={responseTitle}
            text={responseText}
            buttonText={buttonText}
            onButtonClick={close}
        />
    </>;
};

export default ManiaPopup;
